import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Header from 'components/ui/Header';
import { CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { API_ENDPOINTS } from '../../utils/config';

const AssignUserRole = () => {
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [isLoadingUserRoles, setIsLoadingUserRoles] = useState(false);
  const [viewingUserRole, setViewingUserRole] = useState(null);
  const [viewUserDetails, setViewUserDetails] = useState(null);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', role: '', status: 'active', password: '' });
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingDeleteUserRole, setPendingDeleteUserRole] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  const [form, setForm] = useState({
    username: '',
    password: '',
    email: '',
    role: ''
  });

  const canSubmit = useMemo(() => {
    return (
      form.username.trim().length > 0 &&
      form.password.trim().length >= 8 &&
      form.email.trim().length > 0 &&
      form.role.trim().length > 0
    );
  }, [form]);

  const openViewUserRole = async (item) => {
    setViewingUserRole(item);
    setViewUserDetails(null);
    setIsLoadingView(true);
    try {
      // If detailed endpoint exists, fetch; else use item
      setViewUserDetails(item);
    } finally {
      setIsLoadingView(false);
    }
  };

  const closeViewUserRole = () => {
    setViewingUserRole(null);
    setViewUserDetails(null);
  };

  const openEditUserRole = (item) => {
    setEditingUserRole(item);
    setEditForm({
      username: item?.username || '',
      email: item?.email || '',
      role: (typeof item?.role === 'object' ? item?.role?.name : item?.role) || '',
      status: item?.status || 'active',
      password: ''
    });
  };

  const closeEditUserRole = () => {
    setEditingUserRole(null);
  };

  const submitEditUserRole = async (e) => {
    e.preventDefault();
    if (!editingUserRole?.id) return;
    setIsUpdating(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      // Assuming update endpoint; adjust if API differs
      const payload = {
        username: editForm.username,
        email: editForm.email,
        role: editForm.role,
        is_active: editForm.status === 'active'
      };
      const trimmedPassword = editForm.password?.trim();
      if (trimmedPassword && trimmedPassword.length >= 8) {
        payload.password = trimmedPassword;
      }
      const { data } = await axios.patch(API_ENDPOINTS.USER_ROLES_UPDATE(editingUserRole.id), payload, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      // Map response to reflect new status immediately
      const updated = data?.data;
      if (updated?.user_detail) {
        setUserRoles((prev) => prev.map((u) =>
          u.id === editingUserRole.id
            ? {
                ...u,
                username: updated.user_detail.username ?? u.username,
                email: updated.user_detail.email ?? u.email,
                role: updated.role_detail?.name ?? u.role,
                status: updated.user_detail.is_active === false ? 'inactive' : 'active'
              }
            : u
        ));
      } else {
        // Optimistically update local state based on submitted values
        setUserRoles((prev) => prev.map((u) =>
          u.id === editingUserRole.id
            ? {
                ...u,
                username: payload.username ?? u.username,
                email: payload.email ?? u.email,
                role: payload.role ?? u.role,
                status: payload.is_active === false ? 'inactive' : 'active'
              }
            : u
        ));
        // Then refresh from server
        await fetchUserRoles();
      }
      setSuccess(`User "${editForm.username}" updated successfully.`);
      setTimeout(() => setSuccess(''), 3000);
      closeEditUserRole();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to update user');
    } finally {
      setIsUpdating(false);
    }
  };

  const requestDeleteUserRole = (item) => {
    setPendingDeleteUserRole(item);
  };

  const cancelDeleteUserRole = () => {
    setPendingDeleteUserRole(null);
  };

  const confirmDeleteUserRole = async () => {
    if (!pendingDeleteUserRole?.id) return;
    setIsDeleting(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const deletedUsername = pendingDeleteUserRole?.username;
      await axios.delete(API_ENDPOINTS.USER_ROLES_DELETE(pendingDeleteUserRole.id), {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      await fetchUserRoles();
      setPendingDeleteUserRole(null);
      setSuccess(`User "${deletedUsername}" deleted successfully.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to delete user');
      setPendingDeleteUserRole(null);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      setIsLoadingRoles(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(API_ENDPOINTS.ROLES_LIST, {
          headers: {
            'Accept': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        const raw = Array.isArray(data) ? data : (data?.results || data?.data || data?.roles || []);
        const normalized = raw.map((r) => ({
          id: r?.id ?? r?.role_id ?? r?.uuid ?? r?.name,
          name: r?.name ?? r?.title ?? String(r?.id ?? '')
        }));
        setRoles(normalized);
      } catch (err) {
        setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to load roles');
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
    fetchUserRoles();
  }, []);

  const fetchUserRoles = async () => {
    setIsLoadingUserRoles(true);
    // don't clear top-level error; keep separate display
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(API_ENDPOINTS.USER_ROLES_LIST, {
        headers: {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const raw = Array.isArray(data) ? data : (data?.results || data?.data || data?.user_roles || []);
      const normalized = raw.map((item) => ({
        id: item?.id,
        username: item?.user_detail?.username ?? '-',
        email: item?.user_detail?.email ?? '-',
        role: item?.role_detail?.name ?? '-',
        status: item?.user_detail?.is_active === false ? 'inactive' : 'active',
        assignedAt: item?.assigned_at ?? '',
        permissionsCount: item?.role_detail?.permissions_count ?? 0,
        // keep originals for view modal context if needed
        _raw: item
      }));
      setUserRoles(normalized);
    } catch (_) {
      // swallow to avoid noisy UI; keep page usable
    } finally {
      setIsLoadingUserRoles(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Assign User Role</h1>
            <p className="text-muted-foreground mt-1">Select a user and assign a role.</p>
          </div>

          <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Create a new application user and assign a role.</p>
            </div>
            <button
              onClick={() => { setIsCreateOpen(true); setError(''); setSuccess(''); }}
              className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium w-full sm:w-auto justify-center"
            >
              Create New User
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded-md border border-green-300 bg-green-50 text-green-700">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <p className="text-sm">{success}</p>
              </div>
            </div>
          )}

          {isCreateOpen && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1100] p-4">
              <div className="w-full max-w-lg bg-card border border-border rounded-lg p-4 max-h-[90vh] overflow-y-auto">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground">Create New User</h2>
                  {isLoadingRoles && (
                    <span className="text-xs text-muted-foreground">Loading roles…</span>
                  )}
                </div>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!canSubmit) return;
                    setIsSubmitting(true);
                    setError('');
                    setSuccess('');
                    try {
                      const token = localStorage.getItem('token');
                      const payload = {
                        username: form.username.trim(),
                        password: form.password.trim(),
                        email: form.email.trim(),
                        role: form.role.trim()
                      };
                      await axios.post(API_ENDPOINTS.USER_ROLES_CREATE, payload, {
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
                      });
                      setSuccess('User created and role assigned successfully.');
                      setIsCreateOpen(false);
                      setForm({ username: '', password: '', email: '', role: '' });
                      setTimeout(() => setSuccess(''), 2000);
                      fetchUserRoles();
                    } catch (err) {
                      setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to create user');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Username</label>
                    <input
                      type="text"
                      value={form.username}
                      onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                      placeholder="e.g., Johnny"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                      placeholder="johnny@gmail.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showCreatePassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        className="w-full border border-border rounded-md px-3 py-2 pr-10 bg-background text-foreground"
                        placeholder="At least 8 characters"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowCreatePassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showCreatePassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                    <select
                      value={form.role}
                      onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                      required
                    >
                      <option value="" disabled>Select a role</option>
                      {roles.map((r) => (
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={!canSubmit || isSubmitting}
                      className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
                    >
                      {isSubmitting ? 'Creating…' : 'Create User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsCreateOpen(false)}
                      className="inline-flex items-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-lg overflow-hidden mt-8">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">User Roles</h2>
              {isLoadingUserRoles && (
                <span className="text-xs text-muted-foreground">Loading…</span>
              )}
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground whitespace-nowrap">Username</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground whitespace-nowrap">Email</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground whitespace-nowrap">Role</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground whitespace-nowrap">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userRoles.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-sm text-muted-foreground text-center">No users found.</td>
                    </tr>
                  ) : (
                    userRoles.map((u) => (
                      <tr key={u.id} className="border-t border-border hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3 text-foreground">{u.username}</td>
                        <td className="px-4 py-3 text-muted-foreground break-words">{u.email}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.role}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            u.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {u.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => openViewUserRole(u)}
                              className="px-3 py-1 rounded-md border border-border text-sm hover:bg-muted transition-colors"
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => openEditUserRole(u)}
                              className="px-3 py-1 rounded-md border border-border text-sm hover:bg-muted transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => requestDeleteUserRole(u)}
                              className="px-3 py-1 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden">
              {userRoles.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">No users found.</div>
              ) : (
                <div className="divide-y divide-border">
                  {userRoles.map((u) => (
                    <div key={u.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-foreground truncate">{u.username}</h3>
                          <p className="text-xs text-muted-foreground truncate mt-1">{u.email}</p>
                        </div>
                        <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize flex-shrink-0 ${
                          u.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {u.status}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Role:</span>
                          <span className="text-foreground font-medium">{u.role}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => openViewUserRole(u)}
                          className="flex-1 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted transition-colors"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditUserRole(u)}
                          className="flex-1 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => requestDeleteUserRole(u)}
                          className="flex-1 px-3 py-2 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {viewingUserRole && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1100] p-4">
              <div className="w-full max-w-lg bg-card border border-border rounded-lg p-4 max-h-[90vh] overflow-y-auto">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground">User Role Details</h2>
                  {isLoadingView && (
                    <span className="text-xs text-muted-foreground">Loading…</span>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-muted-foreground">Username</div>
                    <div className="text-foreground font-medium">{viewUserDetails?.username || viewingUserRole?.username || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Email</div>
                    <div className="text-foreground">{viewUserDetails?.email || viewingUserRole?.email || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Role</div>
                    <div className="text-foreground">{viewUserDetails?.role?.name || viewUserDetails?.role || viewingUserRole?.role || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Status</div>
                    <div className="text-foreground capitalize">{(viewUserDetails?.status || viewingUserRole?.status || 'active')}</div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={closeViewUserRole}
                      className="inline-flex items-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {editingUserRole && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1100] p-4">
              <div className="w-full max-w-lg bg-card border border-border rounded-lg p-4 max-h-[90vh] overflow-y-auto">
                <div className="mb-3">
                  <h2 className="text-lg font-semibold text-foreground">Edit User Role</h2>
                </div>
                <form onSubmit={submitEditUserRole} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Username</label>
                    <input
                      type="text"
                      value={editForm.username}
                      onChange={(e) => setEditForm((f) => ({ ...f, username: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                    <select
                      value={editForm.role}
                      onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                      required
                    >
                      {roles.map((r) => (
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showEditPassword ? 'text' : 'password'}
                        value={editForm.password}
                        onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                        className="w-full border border-border rounded-md px-3 py-2 pr-10 bg-background text-foreground"
                        placeholder="Leave blank to keep unchanged (min 8 chars)"
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowEditPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={isUpdating}
                      className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
                    >
                      {isUpdating ? 'Saving…' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={closeEditUserRole}
                      className="inline-flex items-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {pendingDeleteUserRole && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1100] p-4">
              <div className="w-full max-w-md bg-card border border-border rounded-lg p-4 max-h-[90vh] overflow-y-auto">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-foreground">Delete User Role</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to delete "{pendingDeleteUserRole?.username}"?
                  This action cannot be undone.
                </p>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    type="button"
                    onClick={cancelDeleteUserRole}
                    className="inline-flex items-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteUserRole}
                    disabled={isDeleting}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium disabled:opacity-60"
                  >
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AssignUserRole;


